import { normalizeNamespaceAndPath } from '~/helpers';
import { useModelsStore, useSettingsStore, useUserStore } from '~/stores';

/**
 * Why a feature is unusable here -- the tri-state that `hasFeatures` cannot express.
 *
 * The distinction that matters is `hidden` vs `locked`. Hidden covers everything the
 * tenant cannot buy their way out of: the wrong game, a custom handler, or anything
 * belonging to physgun (Wisp's in-house set, never sold). Locked is the one case worth
 * showing an admin, because a plan or addon would unlock it.
 *
 * `hasFeatures`/`featuresStateLoaded` in `~/plugins/feature` remain the plain boolean
 * API; this adds the commercial reason on top for the admin paywall.
 */
export type Verdict =
    | { state: 'allowed' }
    | { state: 'hidden' }
    | { state: 'locked'; feature: string; requiresPlan?: string; requiresAddon?: string };

/**
 * Both key casings are accepted deliberately. `admin:`/`general:` verdicts arrive from
 * /api/client/settings as raw JSON and keep the backend's snake_case, while `server:`
 * verdicts ride on the server model through Parser, whose convertDataToCamelCase
 * recurses and rewrites them. Reading only one form silently loses the addon name for
 * half the features -- and `purchasable` has no underscore, so the lock still appears
 * and the failure looks like a missing addon rather than a casing mismatch.
 */
interface FeatureVerdict {
    available: boolean;
    // Only ever sent to admins -- see PanelSpecificFeatures::present().
    purchasable?: boolean;
    requiresPlan?: string;
    requires_plan?: string;
    requiresAddon?: string;
    requires_addon?: string;
}

function verdictData(feature: string): FeatureVerdict | undefined {
    if (feature.startsWith('server:')) {
        return useModelsStore().server?.features?.[feature];
    }

    return useSettingsStore().data?.features?.[feature] as FeatureVerdict | undefined;
}

/**
 * Resolve a single feature key. Bare names normalize against the route being rendered,
 * which is correct at component time; route middleware must pre-qualify via
 * `normalizeGateValue`, since during navigation the current route is still the old one.
 */
export function entitlementFor(feature: string): Verdict {
    const key = normalizeNamespaceAndPath(feature);
    const data = verdictData(key);

    if (data?.available) {
        return { state: 'allowed' };
    }

    // `purchasable` is stripped server-side for non-admins, so this branch is already
    // unreachable for them. The explicit check keeps that true even if the payload
    // shape ever regresses.
    const user = useUserStore().data;
    const isAdmin = !!(user?.rootAdmin || user?.supportOp);

    if (data?.purchasable && isAdmin) {
        return {
            state: 'locked',
            feature: key,
            requiresPlan: data.requiresPlan ?? data.requires_plan,
            requiresAddon: data.requiresAddon ?? data.requires_addon,
        };
    }

    return { state: 'hidden' };
}

/**
 * Verdict for a gate listing several features. `allowed` wins over `locked`, and
 * `locked` over `hidden` -- if any one of them is purchasable there is something worth
 * offering.
 */
export function entitlementForAny(features: string | string[]): Verdict {
    const verdicts = [features].flat().map(entitlementFor);

    return verdicts.find((verdict) => verdict.state === 'allowed') ?? verdicts.find((verdict) => verdict.state === 'locked') ?? { state: 'hidden' };
}
